import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Radio, FormControlLabel, RadioGroup, Checkbox, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";

interface AllergyModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const AllergyModal: React.FC<AllergyModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);
  const selectedOfficeId = useAppSelector((state) => (state as any).auth.selectedOfficeId);
  const selectedOffice = user?.UserAssPriviliges?.find((office: any) => office.ID === selectedOfficeId);
  const offid = Number(selectedOffice?.OFF_ID || user?.UserAssPriviliges?.[0]?.OFF_ID || 2);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [allergyStatus, setAllergyStatus] = useState<"VAR" | "NONE">("VAR");
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  // Tekli seçim için state'ler
  const [substanceId, setSubstanceId] = useState<number | null>(null);
  const [substanceNames, setSubstanceNames] = useState<string>("");
  const [selectedSubstanceItem, setSelectedSubstanceItem] = useState<any>(null);
  const [foodId, setFoodId] = useState<number | null>(null);
  const [foodNames, setFoodNames] = useState<string>("");
  const [selectedFoodItem, setSelectedFoodItem] = useState<any>(null);
  const [otherId, setOtherId] = useState<number | null>(null);
  const [otherNames, setOtherNames] = useState<string>("");
  const [selectedOtherItem, setSelectedOtherItem] = useState<any>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setNote(editData.note || editData.privatenotes || "");
      setIsActive(editData.isactiveallergy === "1");
      // allergystatus: "0" = "NONE", "1" = "VAR"
      // String veya number olarak gelebilir, her ikisini de kontrol et
      if (editData.allergystatus !== undefined && editData.allergystatus !== null) {
        const statusValue = String(editData.allergystatus);
        setAllergyStatus(statusValue === "1" ? "VAR" : "NONE");
      } else {
        // Eğer allergystatus yoksa varsayılan olarak "VAR" set et
        setAllergyStatus("VAR");
      }
      if (editData.allergylastdate) {
        try {
          const date = typeof editData.allergylastdate === 'string' ? parseISO(editData.allergylastdate) : new Date(editData.allergylastdate as any);
          setEndDate(date);
        } catch (e) {
          setEndDate(new Date());
        }
      }
      if (editData.allergytype) {
        if (editData.allergytype === 1) {
          setSubstanceId(editData.pid || null);
          setSubstanceNames(editData.name || "");
          setSelectedSubstanceItem({ id: editData.pid, name: editData.name });
        } else if (editData.allergytype === 2) {
          setOtherId(editData.pid || null);
          setOtherNames(editData.name || "");
          setSelectedOtherItem({ id: editData.pid, name: editData.name });
        } else if (editData.allergytype === 3) {
          setFoodId(editData.pid || null);
          setFoodNames(editData.name || "");
          setSelectedFoodItem({ id: editData.pid, name: editData.name });
        }
      }
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.pdate || editData.ctime) {
        try {
          const dateString = editData.pdate || editData.ctime;
          const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal açıldığında snackbar'ı temizle
  useEffect(() => {
    if (open) {
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setOtherId(null);
      setOtherNames("");
      setSelectedOtherItem(null);
      setSubstanceId(null);
      setSubstanceNames("");
      setSelectedSubstanceItem(null);
      setFoodId(null);
      setFoodNames("");
      setSelectedFoodItem(null);
      setNote("");
      setEndDate(new Date());
      setIsActive(true);
      setAllergyStatus("VAR");
      setOperationDate(new Date());
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Tarih formatını "2025-12-26T09:11:38" formatına çevir (milisaniye ve Z olmadan)
  const formatDateTime = (date: Date | null): string | undefined => {
    if (!date) return undefined;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("allergy.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda update yapılacak
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("allergy.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      // Alerji durumu mapping
      const allergyStatusValue = allergyStatus === "VAR" ? "1" : "0";

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateAllergy({
          id: editData.id,
          note: note || "",
          allergystatus: allergyStatusValue,
          allergytype: editData.allergytype || 0,
          isactiveallergy: isActive ? "1" : "0",
          allergylastdate: formatDateTime(endDate),
        });

        setSnackbarMessage(t("allergy.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("Alerji güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("allergy.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt için
    // En az bir alan seçili olmalı
    const hasSubstance = substanceId !== null;
    const hasFood = foodId !== null;
    const hasOther = otherId !== null;

    if (!hasSubstance && !hasFood && !hasOther) {
      setSnackbarMessage(t("allergy.errors.no-selection"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Alerji durumu mapping
    const allergyStatusValue = allergyStatus === "VAR" ? "1" : "0";

    setLoading(true);
    try {
      let requestData: any = {
        ptId: Number(patientId),
        note: note || "",
        allergyStatus: allergyStatusValue,
        isActiveAllergy: isActive ? "1" : "0",
        allergyLastDate: formatDateTime(endDate),
      };

      // Hangi tip seçildiyse ona göre tip ve ID set et
      if (hasSubstance) {
        requestData.processId = Number(substanceId);
        requestData.allergyType = 1; // Etken Madde
      } else if (hasFood) {
        requestData.processId = Number(foodId);
        requestData.allergyType = 3; // Besin
      } else if (hasOther) {
        requestData.processId = Number(otherId);
        requestData.allergyType = 2; // Diğer
      }

      await mcGeneralDiseaseService.addAllergy(requestData);

      setSnackbarMessage(t("allergy.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Alerji kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("allergy.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("allergy.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Alerji */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              px: 3,
              py: 1,
            }}
          >
            <ByzMuiTypography isTitle={true}>
              {t("allergy.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Alerji Durumu (Var / Bilinen Yok) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.title")} Durumu
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <RadioGroup
                    row
                    value={allergyStatus}
                    onChange={(e) => {
                      setAllergyStatus(e.target.value === "VAR" ? "VAR" : "NONE");
                    }}
                  >
                    <FormControlLabel
                      value="VAR"
                      control={<Radio />}
                      label={t("allergy.allergy-status-var")}
                    />
                    <FormControlLabel
                      value="NONE"
                      control={<Radio />}
                      label={t("allergy.allergy-status-none")}
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            </Grid>

            {/* Bitiş Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.last-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={endDate}
                    onChange={(val) => setEndDate(val as Date | null)}
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Etken Madde */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.active-ingredient")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    value={substanceNames}
                    selectedItem={selectedSubstanceItem}
                    singleSelection={true}
                    disabled={!!editData || foodId !== null || otherId !== null}
                    onChange={(value: any) => {
                      const item = value;
                      if (item) {
                        const id = Number(item?.id || item?.ID || 0);
                        const name = item?.name || item?.NAME || "";
                        setSelectedSubstanceItem(item);
                        setSubstanceId(id);
                        setSubstanceNames(name);
                        // Etken Madde seçildiğinde Besin ve Diğer'i temizle
                        setFoodId(null);
                        setFoodNames("");
                        setSelectedFoodItem(null);
                        setOtherId(null);
                        setOtherNames("");
                        setSelectedOtherItem(null);
                      } else {
                        setSelectedSubstanceItem(null);
                        setSubstanceId(null);
                        setSubstanceNames("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-allergy-active-ingredient"
                    method="GET"
                    dialogTitle={t("allergy.dialog-title-active")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "code", headerName: "Kod", width: 120 },
                      { field: "name", headerName: t("allergy.active-ingredient"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Besin */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.nutrient")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    value={foodNames}
                    selectedItem={selectedFoodItem}
                    singleSelection={true}
                    disabled={!!editData || substanceId !== null || otherId !== null}
                    onChange={(value: any) => {
                      const item = value;
                      if (item) {
                        const id = Number(item?.id || item?.ID || 0);
                        const name = item?.name || item?.NAME || "";
                        setSelectedFoodItem(item);
                        setFoodId(id);
                        setFoodNames(name);
                        // Besin seçildiğinde Etken Madde ve Diğer'i temizle
                        setSubstanceId(null);
                        setSubstanceNames("");
                        setSelectedSubstanceItem(null);
                        setOtherId(null);
                        setOtherNames("");
                        setSelectedOtherItem(null);
                      } else {
                        setSelectedFoodItem(null);
                        setFoodId(null);
                        setFoodNames("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-allergy-nutrient"
                    method="GET"
                    dialogTitle={t("allergy.dialog-title-nutrient")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "code", headerName: "Kod", width: 120 },
                      { field: "name", headerName: t("allergy.nutrient"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Diğer */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.other")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    value={otherNames}
                    selectedItem={selectedOtherItem}
                    singleSelection={true}
                    disabled={!!editData || substanceId !== null || foodId !== null}
                    onChange={(value: any) => {
                      const item = value;
                      if (item) {
                        const id = Number(item?.id || item?.ID || 0);
                        const name = item?.name || item?.NAME || "";
                        setSelectedOtherItem(item);
                        setOtherId(id);
                        setOtherNames(name);
                        // Diğer seçildiğinde Etken Madde ve Besin'i temizle
                        setSubstanceId(null);
                        setSubstanceNames("");
                        setSelectedSubstanceItem(null);
                        setFoodId(null);
                        setFoodNames("");
                        setSelectedFoodItem(null);
                      } else {
                        setSelectedOtherItem(null);
                        setOtherId(null);
                        setOtherNames("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-allergy-other"
                    method="POST"
                    postData={{
                      offid: offid,
                    }}
                    dialogTitle={t("allergy.dialog-title-other")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("allergy.other"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Aktif (1 / 0) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.is-active")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                    }
                    label="Aktif"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("allergy.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("allergy.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default AllergyModal;
